import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, LogIn } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { GOOGLE_WORKSPACE_PROVIDER } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';

export default function LoginPage() {
  const { login, isAuthenticated, isLoading } = useAuth();
  const { logoUrl } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination from location state
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  useEffect(() => {
    // If already authenticated, redirect to intended destination
    if (!isLoading && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const handleGoogleWorkspaceLogin = async () => {
    try {
      await login(GOOGLE_WORKSPACE_PROVIDER);
    } catch (error) {
      console.error('Google Workspace login error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="BSides Boulder logo"
                className="w-20 h-20 object-contain mx-auto mb-4 rounded-lg"
              />
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900">BSides Boulder</h1>
            <p className="text-gray-600 mt-2">Organizer Toolkit</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleGoogleWorkspaceLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 disabled:opacity-60 text-gray-700 font-medium py-3 px-4 rounded-lg border border-gray-300 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google Workspace
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-400">or</span>
              </div>
            </div>

            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              <LogIn className="w-5 h-5" />
              {isLoading ? 'Loading...' : 'Sign in with email & password'}
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Organizers should use Google Workspace. Password sign-in is for break-glass access.</p>
          </div>
        </div>

        <p className="text-center text-sm text-gray-400 mt-4">
          Protected by AWS Cognito
        </p>
      </div>
    </div>
  );
}
